import React, { useState, useRef } from 'react';
import { Box, Chip, Popover, TableCell, useTheme, Typography } from '@mui/material';
import { getSkillColors as getSkillColorFromName } from '../../helpers/color';
import useScreenWidth from '../../hooks/useScreenWidth';

const SkillChipsPopover = ({
  skills = [],
  width = '25%',
  columnWidth = 200,
  setMobileVisibleSkills,
  isAnalyticLabel,
  totalSkills = 0,
  cellSx,
  wrapSingle = false,
  emptyLabel = "—",
}) => {
  const [popoverState, setPopoverState] = useState({
    isOpen: false,
    anchorEl: null,
  });
  const theme = useTheme();
  const { screenWidth } = useScreenWidth();
  const Wrapper = setMobileVisibleSkills ? "div" : TableCell
  const popoverRef = useRef(null);
  skills = wrapSingle ? skills.filter(Boolean) : skills.sort((a, b) => a.length - b.length)

  const getVisibleSkills = () => {
    let totalWidth = 0;
    let visibleSkills = [];
    let availableWidth = columnWidth;

    if (setMobileVisibleSkills) {
      if (screenWidth < 640) {
        availableWidth = 10;
      } else if (screenWidth < 768) {
        availableWidth = 10;
      } else {
        availableWidth = 200;
      }
    }

    if (skills) {
      const maxVisible = wrapSingle ? 3 : skills.length;
      const gap = 8;
      const countWidth = 48;
      for (let i = 0; i < skills.length && visibleSkills.length < maxVisible; i++) {
        if (!skills[i]) continue;
        const skillWidth = skills[i].length * 8 + 24;
        const moreRemain = i < skills.length - 1;
        const reserve = moreRemain ? countWidth + gap : 0;
        if (wrapSingle && visibleSkills.length === 0 && skillWidth + reserve > availableWidth) {
          visibleSkills.push(skills[i]);
          break;
        }
        if (totalWidth + skillWidth + reserve > availableWidth) break;
        totalWidth += skillWidth + gap;
        visibleSkills.push(skills[i]);
      }
    }
    return visibleSkills;
  };

  const handlePopoverToggle = (event) => {
    event.stopPropagation();
    setPopoverState({
      isOpen: !popoverState.isOpen,
      anchorEl: popoverState.isOpen ? null : event.currentTarget,
    });
  };

  const handlePopoverClose = (event) => {
    event.stopPropagation();
    setPopoverState({
      isOpen: false,
      anchorEl: null,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      handlePopoverClose(event);
    }
  };

  const visibleSkills = getVisibleSkills();
  const hiddenCount = skills.length - visibleSkills.length;
  const wrapOnlySkill = wrapSingle && skills.length === 1;

  const getSkillColors = (skill, idx) => {
    return getSkillColorFromName(skill);
  };

  return (
    <Wrapper sx={[{ width: wrapSingle ? columnWidth : width, maxWidth: wrapSingle ? columnWidth : undefined, p: 1 }, cellSx]}>
      {skills && skills.length > 0 ? (
        <>
          <Box
            display="flex"
            flexWrap={wrapSingle ? "nowrap" : "wrap"}
            justifyContent={{ xs: setMobileVisibleSkills && 'center', lg: 'flex-start' }}
            alignItems="center"
            gap={1}
            sx={wrapSingle ? { width: columnWidth, maxWidth: columnWidth, minWidth: 0 } : undefined}
          >
            {isAnalyticLabel &&
              <p className='w-12'>
                {skills.length}/{totalSkills}
              </p>
            }
            {visibleSkills.map((skill, idx) => {
              const { bgColor, borderColor } = getSkillColors(skill, idx);
              return (
                <Chip
                  key={idx}
                  label={skill}
                  title={wrapSingle ? skill : undefined}
                  size="small"
                  sx={{
                    border: '1px solid',
                    color: 'rgba(0, 0, 0, 1)',
                    backgroundColor: bgColor,
                    borderRadius: wrapSingle ? '25px' : '50px',
                    borderColor: borderColor,
                    ...(wrapOnlySkill && {
                      height: 'auto',
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                    }),
                    ...(wrapSingle && !wrapOnlySkill && visibleSkills.length === 1 && {
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                    }),
                    ...(wrapSingle && visibleSkills.length > 1 && {
                      flexShrink: 0,
                    }),
                    '& .MuiChip-label': {
                      padding: '0 10px',
                      ...(wrapOnlySkill && {
                        display: 'block',
                        overflow: 'visible',
                        textOverflow: 'clip',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.4,
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      }),
                      ...(wrapSingle && !wrapOnlySkill && visibleSkills.length === 1 && {
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                    },
                  }}
                />
              );
            })}
            {hiddenCount > 0 && (
              <Chip
                label={screenWidth < 640 && setMobileVisibleSkills ? `${hiddenCount}` : `+${hiddenCount}`}
                size="small"
                onClick={handlePopoverToggle}
                role="button"
                aria-expanded={popoverState.isOpen}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.main,
                  border: `1px solid ${theme.palette.secondary.main}`,
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: '2px solid #000000',
                    outlineOffset: '2px',
                  },
                  flexShrink: wrapSingle ? 0 : undefined,
                  '& .MuiChip-label': {
                    padding: '0 10px',
                  },
                }}
              />
            )}
          </Box>

          <Popover
            ref={popoverRef}
            onClick={e => e.stopPropagation()}
            open={popoverState.isOpen}
            anchorEl={popoverState.anchorEl}
            role='button'
            aria-expanded={popoverState.anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                p: 2,
                maxWidth: wrapSingle ? 360 : 300,
                maxHeight: 150,
                overflowY: 'auto',
                backgroundColor: theme.palette.primary.main,
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: '8px',
                '&:focus-visible': {
                  outline: '2px solid #000000',
                  outlineOffset: '2px',
                },
              },
              className: 'always-show-scrollbar-horizontal',
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              gap={1}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              sx={{
                ...(wrapSingle && { maxWidth: 328 }),
                '&:focus-visible': {
                  outline: '2px solid #000000',
                  outlineOffset: '2px',
                },
              }}
            >
              {skills.map((skill, idx) => {
                const { bgColor, borderColor } = getSkillColors(skill, idx);
                return (
                  <Chip
                    key={idx}
                    label={skill}
                    size="small"
                    tabIndex={0}
                    sx={{
                      backgroundColor: bgColor,
                      color: '#000',
                      fontSize: '0.75rem',
                      borderRadius: wrapSingle ? '25px' : '50px',
                      border: '1px solid',
                      borderColor: borderColor,
                      ...(wrapSingle && {
                        height: 'auto',
                        maxWidth: '100%',
                        whiteSpace: 'normal',
                      }),
                      '&:focus-visible': {
                        outline: '2px solid #000000',
                        outlineOffset: '2px',
                      },
                      ...(wrapSingle && {
                        '& .MuiChip-label': {
                          display: 'block',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.4,
                          paddingTop: '4px',
                          paddingBottom: '4px',
                        },
                      }),
                    }}
                  />
                );
              })}
            </Box>
          </Popover>
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            fontSize: "16px",
            color: theme.palette.text.main,
            display: 'flex',
            gap: '8px'
          }}
        >
          {isAnalyticLabel &&
            <p className='w-12'>
              {skills.length}/{totalSkills}
            </p>
          }
          <Typography>{emptyLabel}</Typography>
        </Box>
      )}
    </Wrapper>
  );
};

export default SkillChipsPopover;
